const KST_TIME_ZONE = 'Asia/Seoul'
const DAY_MS = 86_400_000

function phase(id, label, startAt, endAt, extra = {}) {
  return Object.freeze({ id, label, startAt, endAt, ...extra })
}

export const registrationSchedule2026Fall = Object.freeze({
  semesterKey: '2026-2',
  semesterLabel: '2026년 2학기',
  preRegistration: phase('pre-registration', '예비수강신청', '2026-08-20T09:00:00+09:00', '2026-08-21T17:00:00+09:00'),
  byGrade: Object.freeze({
    4: phase('main-registration', '4학년 본수강신청', '2026-08-25T09:00:00+09:00', '2026-08-26T06:00:00+09:00', { grade: 4 }),
    3: phase('main-registration', '3학년 본수강신청', '2026-08-26T09:00:00+09:00', '2026-08-27T06:00:00+09:00', { grade: 3 }),
    2: phase('main-registration', '2학년 본수강신청', '2026-08-27T09:00:00+09:00', '2026-08-28T06:00:00+09:00', { grade: 2 }),
    1: phase('main-registration', '1학년 본수강신청', '2026-08-28T09:00:00+09:00', '2026-08-29T06:00:00+09:00', { grade: 1 }),
  }),
  adjustment: phase('adjustment', '정정기간', '2026-09-01T09:00:00+09:00', '2026-09-07T06:00:00+09:00'),
  finalAdjustment: phase('final-adjustment', '최종정정', '2026-09-09T09:00:00+09:00', '2026-09-10T18:00:00+09:00'),
  cancellation: phase('cancellation', '수강취소', '2026-09-17T09:00:00+09:00', '2026-09-18T17:00:00+09:00', { note: '수강취소는 PC 웹에서만 가능해요.' }),
  sourceUrl: 'https://www.suwon.ac.kr/index.html?act=view&bbsno=88333&boardno=667&menuno=674&siteno=37',
  sourceLabel: '2026학년도 2학기 수강신청 안내',
})

// Existing planning code consumes this shape. Keep it as an adapter while the richer
// schedule above remains the single source of truth.
export const COURSE_REGISTRATION_SCHEDULE = Object.freeze({
  semesterKey: registrationSchedule2026Fall.semesterKey,
  semesterLabel: registrationSchedule2026Fall.semesterLabel,
  phases: [registrationSchedule2026Fall.byGrade[4]],
})

export function normalizeRegistrationGrade(value) {
  const grade = Number(value)
  return Number.isInteger(grade) && grade >= 1 && grade <= 4 ? grade : null
}

function kstDateParts(date) {
  const values = new Intl.DateTimeFormat('en-CA', {
    timeZone: KST_TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date)
  return Object.fromEntries(values.filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]))
}

export function calendarDaysUntil(target, now) {
  const targetParts = kstDateParts(target)
  const nowParts = kstDateParts(now)
  return Math.round((Date.UTC(targetParts.year, targetParts.month - 1, targetParts.day) - Date.UTC(nowParts.year, nowParts.month - 1, nowParts.day)) / DAY_MS)
}

export function formatKstMoment(value) {
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: KST_TIME_ZONE,
    month: 'long', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(value).replace(' (', '(')
}

function formatKstDateTime(value) {
  const parts = new Intl.DateTimeFormat('ko-KR', {
    timeZone: KST_TIME_ZONE,
    month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(value)
  const item = type => parts.find(part => part.type === type)?.value
  return `${item('month')} ${item('day')}일 ${item('hour')}:${item('minute')}`
}

function formatKstRange(startAt, endAt) {
  return `${formatKstMoment(new Date(startAt))} → ${formatKstMoment(new Date(endAt))}`
}

export function phaseState(phase, now) {
  const start = new Date(phase.startAt)
  const end = new Date(phase.endAt)
  if (now < start) return 'upcoming'
  if (now <= end) return 'active'
  return 'completed'
}

export function getRegistrationPhases({ grade, schedule = registrationSchedule2026Fall, now = new Date() }) {
  const normalizedGrade = normalizeRegistrationGrade(grade)
  if (!normalizedGrade) return []
  const phases = [
    schedule.preRegistration,
    schedule.byGrade[normalizedGrade],
    schedule.adjustment,
    schedule.finalAdjustment,
    schedule.cancellation,
  ]
  const states = phases.map(item => phaseState(item, now))
  const hasActivePhase = states.includes('active')
  const nextIndex = hasActivePhase ? -1 : states.indexOf('upcoming')

  return phases.map((item, index) => ({
    ...item,
    state: states[index] === 'active' ? 'active' : index === nextIndex ? 'next' : states[index],
  }))
}

/** Returns the current personalized registration state using Korea Standard Time.
 * Counts down through main-registration → adjustment → final-adjustment → cancellation,
 * in that order, then reports the whole semester's registration schedule as finished.
 * Pre-registration stays informational because the primary action remains timetable
 * preparation until the personalized main-registration window starts. */
export function getRegistrationStatus({ now = new Date(), grade, schedule = registrationSchedule2026Fall }) {
  const normalizedGrade = normalizeRegistrationGrade(grade)
  if (!normalizedGrade) {
    return { currentPhase: 'grade-selection', targetDate: null, daysLeft: null, title: '학년을 선택해 주세요', subtitle: '내 학년 본수강신청 일정을 알려드릴게요.', isActive: false, isCompleted: false }
  }

  const stages = [
    { ...schedule.byGrade[normalizedGrade], activeTitle: '수강신청 진행 중' },
    { ...schedule.adjustment, activeTitle: '정정기간 진행 중' },
    { ...schedule.finalAdjustment, activeTitle: '최종정정 진행 중' },
    { ...schedule.cancellation, activeTitle: '수강취소 진행 중' },
  ]

  for (const stage of stages) {
    const state = phaseState(stage, now)
    if (state === 'active') {
      return {
        currentPhase: stage.id, targetDate: stage.startAt, daysLeft: 0,
        title: stage.activeTitle,
        subtitle: `${stage.label} · ${formatKstDateTime(new Date(stage.endAt))}까지`,
        isActive: true, isCompleted: false,
      }
    }
    if (state === 'upcoming') {
      const daysLeft = calendarDaysUntil(new Date(stage.startAt), now)
      return {
        currentPhase: stage.id === 'main-registration' ? 'preparation' : stage.id,
        targetDate: stage.startAt, daysLeft,
        title: daysLeft === 0 ? '오늘 09:00 시작' : `D-${daysLeft}`,
        subtitle: `${stage.label}\n${formatKstRange(stage.startAt, stage.endAt)}`,
        isActive: false, isCompleted: false,
      }
    }
  }

  return {
    currentPhase: 'completed', targetDate: null, daysLeft: null,
    title: `${schedule.semesterLabel} 수강신청 일정 종료`, subtitle: null,
    isActive: false, isCompleted: true,
  }
}

export function resolveCourseRegistrationPhase(schedule = COURSE_REGISTRATION_SCHEDULE, now = new Date()) {
  const hasSchedule = Boolean(schedule)
  const status = getRegistrationStatus({ now, grade: 4, schedule: registrationSchedule2026Fall })
  if (status.currentPhase === 'preparation') return { id: 'preparation', label: '준비 기간', dDay: status.daysLeft, nextPhase: registrationSchedule2026Fall.byGrade[4], isScheduleVerified: hasSchedule }
  if (status.currentPhase === 'main-registration') return { ...registrationSchedule2026Fall.byGrade[4], id: 'main-registration', label: '본 수강신청', dDay: 0, isScheduleVerified: hasSchedule }
  return { id: 'unconfirmed', label: '일정 확인 중', dDay: null, isScheduleVerified: false }
}
